import React, { useEffect, useState } from "react";
import {
  X,
  Save,
  AlertCircle,
  Loader2,
  Users,
  Clock,
  CheckCircle,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import type { EventCheckoutSessionAttendee } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutSessionDetails/types";
import { RootState } from "@/app/rootReducer";
import { AttendeeWaitlistRequest } from "@/modules/eventRegistration/features/EventRegistration/api/updateEventCheckoutAttendeeWaitlist/types";
import {
  getEventCheckoutSessionDetailsAction,
  updateEventCheckoutAttendeeWaitlistAction,
} from "@/modules/eventRegistration/features/EventRegistration/slices/eventCheckoutSlice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  allAttendees: EventCheckoutSessionAttendee[];
  eventName: string;
  nonWaitlistedAttendeesInCheckout: number;
  minimumOffWaitlist?: number;
  checkoutSessionUuid: string;
}

function WaitlistModal({
  isOpen,
  onClose,
  allAttendees,
  eventName,
  nonWaitlistedAttendeesInCheckout,
  checkoutSessionUuid,
}: WaitlistModalProps) {
  const dispatch = useDispatch();
  const loadingUpdateWaitlist = useSelector(
    (state: RootState) => state.eventCheckout.loadingUpdateWaitlist,
  );
  const updateWaitlistError = useSelector(
    (state: RootState) => state.eventCheckout.updateWaitlistError,
  );

  const [localAttendees, setLocalAttendees] = useState<
    EventCheckoutSessionAttendee[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when modal opens
  useEffect(() => {
    const selectedAttendees = allAttendees.filter((a) => a.isSelected);
    setLocalAttendees(selectedAttendees);
    setHasChanges(false);
  }, [allAttendees, isOpen]);

  const confirmedCount = localAttendees.filter((a) => !a.isWaitlist).length;
  const availableSeats = nonWaitlistedAttendeesInCheckout;
  const seatsRemaining = availableSeats - confirmedCount;

  function handleToggle(attendeeId: number, currentlyWaitlisted: boolean) {
    // If trying to move from waitlist to confirmed, check if seats available
    if (currentlyWaitlisted && seatsRemaining <= 0) {
      return; // Can't confirm - no seats available
    }

    setLocalAttendees((prev) =>
      prev.map((attendee) =>
        attendee.id === attendeeId
          ? { ...attendee, isWaitlist: !currentlyWaitlisted }
          : attendee,
      ),
    );
    setHasChanges(true);
  }

  function handleSave() {
    const attendeeUpdates: AttendeeWaitlistRequest[] = localAttendees.map(
      (attendee) => ({
        attendeeId: attendee.id,
        isWaitlist: attendee.isWaitlist,
      }),
    );
    dispatch(
      updateEventCheckoutAttendeeWaitlistAction(
        checkoutSessionUuid,
        { attendees: attendeeUpdates },
        () => {
          dispatch(getEventCheckoutSessionDetailsAction(checkoutSessionUuid));
          onClose();
        },
      ),
    );
  }

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Attendees</DialogTitle>
          <DialogDescription>{eventName}</DialogDescription>
        </DialogHeader>

        {/* Seat availability indicator */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">Confirmed Seats</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-bold ${
                seatsRemaining === 0
                  ? "text-amber-600"
                  : "text-green-600 dark:text-green-400"
              }`}
            >
              {confirmedCount} of {availableSeats}
            </span>
            {seatsRemaining === 0 && (
              <Badge
                className="text-amber-600 border-amber-300"
                variant="outline"
              >
                Full
              </Badge>
            )}
          </div>
        </div>

        {/* Error message */}
        {updateWaitlistError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{updateWaitlistError}</AlertDescription>
          </Alert>
        )}

        {/* Attendee list */}
        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-2">
            {localAttendees.map((attendee) => {
              const isWaitlisted = attendee.isWaitlist;
              const canConfirm = !isWaitlisted || seatsRemaining > 0;

              return (
                <div
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isWaitlisted
                      ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                      : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  }`}
                  key={attendee.id}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        isWaitlisted
                          ? "bg-amber-100 dark:bg-amber-800"
                          : "bg-green-100 dark:bg-green-800"
                      }`}
                    >
                      {isWaitlisted ? (
                        <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        {attendee.firstName} {attendee.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {attendee.email || "No email"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    <span
                      className={`text-xs font-medium ${
                        isWaitlisted
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-green-600 dark:text-green-400"
                      }`}
                    >
                      {isWaitlisted ? "Waitlist" : "Confirmed"}
                    </span>
                    <Switch
                      checked={!isWaitlisted}
                      disabled={!canConfirm && isWaitlisted}
                      onCheckedChange={() =>
                        handleToggle(attendee.id, isWaitlisted)
                      }
                      title={
                        !canConfirm && isWaitlisted
                          ? "No confirmed seats available"
                          : isWaitlisted
                            ? "Move to confirmed"
                            : "Move to waitlist"
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        {/* Help text */}
        {seatsRemaining === 0 &&
          localAttendees.some((a) => a.isWaitlist) && (
            <p className="text-xs text-muted-foreground">
              To confirm a waitlisted attendee, first move someone else to the
              waitlist.
            </p>
          )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div>
            {hasChanges && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Unsaved changes
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button disabled={!hasChanges || loadingUpdateWaitlist} onClick={handleSave}>
              {loadingUpdateWaitlist ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WaitlistModal;
